import type { LineupHitter } from '../state/selectors';
import { teamColor } from '../state/teamColors';
import PlayerAvatar from './PlayerAvatar';
import type { PlayerSeed } from './PlayerModal';
import styles from './Lineup.module.css';

interface LineupProps {
  side: 'away' | 'home';
  teamCode: string;
  teamAbbr: string;
  hitters: LineupHitter[];
  /** Click handler that receives the full {id, name, jersey, position} seed
   *  so the PlayerModal heading paints synchronously from props. */
  onPlayerClick?: (seed: PlayerSeed) => void;
}

export default function Lineup({ side, teamCode, teamAbbr, hitters, onPlayerClick }: LineupProps): JSX.Element {
  const { primary } = teamColor(teamCode);
  return (
    <section
      aria-label={`${teamAbbr} lineup`}
      data-testid={`lineup-${side}`}
      className={styles.lineup}
    >
      <h2 className={styles.heading} style={{ background: primary }}>
        {teamAbbr} Batting
      </h2>
      <ol className={styles.list}>
        {hitters.map((h) => {
          const rowClass = h.isCurrentBatter ? `${styles.row} ${styles.rowCurrent}` : styles.row;
          const nameClass = h.isCurrentBatter
            ? `${styles.nameText} ${styles.nameTextCurrent}`
            : styles.nameText;
          return (
            <li
              key={h.id}
              data-testid={`lineup-${side}-row-${h.battingOrder}`}
              data-current-batter={h.isCurrentBatter ? 'true' : 'false'}
              className={rowClass}
            >
              <PlayerAvatar personId={h.id} name={h.name} size={240} displaySize={52} />
              <span className={styles.order}>{h.battingOrder}</span>
              <span className={styles.jersey} data-testid={`lineup-${side}-jersey-${h.id}`}>
                {h.jersey ? `#${h.jersey}` : ''}
              </span>
              <button
                type="button"
                onClick={() =>
                  onPlayerClick?.({ id: h.id, name: h.name, jersey: h.jersey, position: h.position })
                }
                className={styles.name}
                data-testid={`lineup-${side}-name-${h.id}`}
              >
                <span className={nameClass ?? ''}>{h.name}</span>
                {h.position && <span className={styles.position}>{h.position}</span>}
              </button>
              <span className={styles.summary}>{h.battingSummary ?? ''}</span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
